import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";

const PENDING_USER_DATA_KEY = "manucalc_pending_user_data_sync_v1";

let activeUserId = "";
let unsubscribers = [];
let reconnectHandler = null;

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const emit = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("manucalc:user-data-sync-status", {
    detail: {
      at: new Date().toISOString(),
      userId: activeUserId,
      ...detail,
    },
  }));
};

const collectionRef = (userId, collectionName) =>
  collection(db, "users", userId, collectionName);

const documentRef = (userId, collectionName, id) =>
  doc(db, "users", userId, collectionName, id);

const readPending = () => {
  if (typeof window === "undefined") return [];
  return safeParse(window.localStorage.getItem(PENDING_USER_DATA_KEY), []);
};

export const getPendingUserDataSyncCount = () => readPending().length;

const writePending = (items) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PENDING_USER_DATA_KEY, JSON.stringify(items));
};

const pendingKey = (item) => `${item.action}:${item.collectionName}:${item.id}`;

const enqueuePending = (item) => {
  const key = pendingKey(item);
  writePending([
    ...readPending().filter((existing) => pendingKey(existing) !== key),
    { ...item, queuedAt: new Date().toISOString() },
  ].slice(-500));
};

const removePending = (item) => {
  const key = pendingKey(item);
  writePending(readPending().filter((existing) => pendingKey(existing) !== key));
};

const sortByUpdatedAt = (records = []) =>
  [...records].sort((a, b) => new Date(b.updatedAt || b.timestamp || 0) - new Date(a.updatedAt || a.timestamp || 0));

export const writeUserCloudRecord = async (collectionName, record, user = null) => {
  const userId = user?.uid || activeUserId;
  if (!userId || !collectionName || !record?.id) {
    if (record?.id) enqueuePending({ action: "save", collectionName, id: record.id, record });
    return;
  }

  const payload = {
    ...record,
    id: record.id,
    ownerId: userId,
    updatedAt: record.updatedAt || new Date().toISOString(),
    updatedAtServer: serverTimestamp(),
    deleted: false,
  };

  try {
    await setDoc(documentRef(userId, collectionName, record.id), payload, { merge: true });
    removePending({ action: "save", collectionName, id: record.id });
    emit({ status: "synced", collectionName, recordId: record.id });
  } catch (error) {
    enqueuePending({ action: "save", collectionName, id: record.id, record });
    emit({ status: "error", collectionName, recordId: record.id, message: error.message });
  }
};

export const deleteUserCloudRecord = async (collectionName, id, user = null) => {
  const userId = user?.uid || activeUserId;
  if (!userId || !collectionName || !id) {
    if (id) enqueuePending({ action: "delete", collectionName, id });
    return;
  }

  try {
    await deleteDoc(documentRef(userId, collectionName, id));
    removePending({ action: "delete", collectionName, id });
    emit({ status: "synced", collectionName, recordId: id });
  } catch (error) {
    enqueuePending({ action: "delete", collectionName, id });
    emit({ status: "error", collectionName, recordId: id, message: error.message });
  }
};

const flushPending = (user) => {
  readPending().forEach((item) => {
    if (item.action === "save") writeUserCloudRecord(item.collectionName, item.record, user);
    if (item.action === "delete") deleteUserCloudRecord(item.collectionName, item.id, user);
  });
};

export const stopUserDataCloudSync = () => {
  unsubscribers.forEach((unsubscribe) => unsubscribe?.());
  if (reconnectHandler && typeof window !== "undefined") {
    window.removeEventListener("online", reconnectHandler);
  }
  unsubscribers = [];
  reconnectHandler = null;
  activeUserId = "";
};

export const startUserDataCloudSync = ({ user, stores = [] } = {}) => {
  stopUserDataCloudSync();
  if (!user?.uid) return () => {};

  activeUserId = user.uid;
  if (typeof window !== "undefined") {
    reconnectHandler = () => {
      const pendingCount = readPending().length;
      if (!pendingCount) return;
      emit({ status: "syncing", message: `Connection restored. Retrying ${pendingCount} queued account changes...` });
      flushPending(user);
    };
    window.addEventListener("online", reconnectHandler);
  }
  emit({ status: "syncing", message: "Connecting user data sync..." });

  unsubscribers = stores
    .filter((store) => store.collectionName && store.storageKey)
    .map((store) =>
      onSnapshot(
        collectionRef(user.uid, store.collectionName),
        (snapshot) => {
          const cloudRecords = snapshot.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .filter((item) => item.deleted !== true);
          const localRecords = safeParse(window.localStorage.getItem(store.storageKey), []);
          const merged = new Map();
          [...localRecords, ...cloudRecords].forEach((record) => {
            if (!record?.id) return;
            const current = merged.get(record.id);
            const currentTime = new Date(current?.updatedAt || current?.timestamp || 0).getTime();
            const recordTime = new Date(record.updatedAt || record.timestamp || 0).getTime();
            if (!current || recordTime >= currentTime) merged.set(record.id, record);
          });
          const next = sortByUpdatedAt([...merged.values()]).slice(0, store.maxRecords || 250);
          window.localStorage.setItem(store.storageKey, JSON.stringify(next));
          window.dispatchEvent(new CustomEvent(store.eventName, { detail: { source: "cloud", records: next } }));
          emit({ status: "synced", collectionName: store.collectionName });
        },
        (error) => {
          emit({ status: "error", collectionName: store.collectionName, message: error.message });
        }
      )
    );

  flushPending(user);
  emit({ status: "synced", message: "User data sync connected." });
  return stopUserDataCloudSync;
};
